/**
 * Deleting an account is two deletions, and the ORDER and the FAILURES both matter.
 *
 * The server data has to go first, because purging it needs a valid token and deleting the
 * Cognito account is what destroys that token. Get the order wrong and the data is
 * unreachable forever — there is no longer an account that can authenticate to ask for it.
 *
 * Which is exactly what made the old code dangerous. It was:
 *
 *     await platform.deleteAccount().catch(() => {});
 *     await deleteAccount();
 *
 * so a purge that failed for ANY reason — a 500, a throttle, a dropped connection — was
 * swallowed, and the auth account was deleted anyway. The person was told "this permanently
 * deletes your account and all your data", and what actually happened was that their data
 * survived, orphaned, with the only credential that could ever reach it destroyed. The
 * promise and the outcome were opposites, and nothing anywhere recorded it.
 *
 * The server had already done its part properly: it returns `visitorDataSkipped` when it
 * could not reach the visitor half for some app, with the comment "a partial deletion
 * reporting success is how you come to believe data is gone when it is not". The client
 * discarded that too. Both halves were right and the seam between them was wrong.
 *
 * So: the auth account is deleted ONLY after the data is confirmed gone, and every outcome
 * is named rather than collapsed into success.
 */
export type DeletionOutcome =
  /** Data purged, account gone. The only clean ending. */
  | { status: 'deleted' }
  /** Account gone, but the server said part of the data could not be reached. Say so —
   *  quietly rounding this up to success is the failure this type exists to prevent. */
  | { status: 'deleted_with_remainder'; message: string }
  /** Data is gone but the account itself survived. Safe to retry, and they must be told,
   *  because from the outside it looks like nothing happened at all. */
  | { status: 'account_remains'; message: string }
  /** Nothing was deleted. The account is untouched, so retrying costs them nothing. */
  | { status: 'kept'; message: string };

export interface DeleteAccountDeps {
  /** `DELETE /me/account` — purges the server data while the token is still valid. */
  purge: () => Promise<{ deleted?: number; visitorDataSkipped?: boolean }>;
  /** Cognito self-delete. Destroys the token, so this runs last. */
  deleteAuth: () => Promise<void>;
}

function reason(e: unknown, fallback: string): string {
  const m = e instanceof Error ? e.message.trim() : '';
  return m || fallback;
}

export async function runAccountDeletion(deps: DeleteAccountDeps): Promise<DeletionOutcome> {
  let result: { deleted?: number; visitorDataSkipped?: boolean };
  try {
    result = await deps.purge();
  } catch (e) {
    // Stop here. Deleting the account now would strand the data permanently, and the whole
    // point of refusing is that the person can try again in a minute.
    return {
      status: 'kept',
      message: `${reason(e, 'Your data could not be deleted.')} Nothing was deleted — please try again.`,
    };
  }

  try {
    await deps.deleteAuth();
  } catch (e) {
    return {
      status: 'account_remains',
      message: `Your data was deleted, but the account itself could not be closed. ${reason(e, 'Please try again.')}`,
    };
  }

  if (result?.visitorDataSkipped) {
    return {
      status: 'deleted_with_remainder',
      message:
        'Your account is closed and your data was deleted, but some of it in other people’s apps could not be reached. Contact support and it will be removed.',
    };
  }
  return { status: 'deleted' };
}

/**
 * The title shown above each outcome's message.
 *
 * A separate decision from the message, and it was collapsed into two states while the
 * outcome type has four: anything that was not `kept` got "Account deleted". So
 * `account_remains` — data gone, login still there — was announced as "Account deleted"
 * directly above a body saying the account could NOT be closed. The four-state type exists
 * precisely to stop that, and the title ignored it.
 *
 * `deleted` is absent on purpose: success shows no alert at all.
 */
export function deletionAlertTitle(
  status: Exclude<DeletionOutcome['status'], 'deleted'>,
): string {
  switch (status) {
    case 'kept':
      // Nothing happened. Saying "deleted" here would be the worst of the four.
      return 'Account not deleted';
    case 'account_remains':
      // True and in this order: the data is what they came to remove, and it is gone.
      return 'Your data is deleted';
    case 'deleted_with_remainder':
      // The account IS closed, so the title is honest; the body carries the remainder.
      return 'Account deleted';
  }
}
